import { normalizeWidgetTheme, APIURL, cancelSelectEvent, getCSSStyles, getWidgetDataURI, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, isEmptyWidgetData } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseCryptoTopMovers {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private widgetIndex: number = 0;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }


  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.attachStyles();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';
    
    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-testing-id="crypto-top-movers-widget-${this.widgetIndex}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} Top Movers</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            <div class="item tabsDv padHt type-tabs">
              <button class="tab active" data-type="loser" data-testing-id="tab-loser">Loser</button>
              <button class="tab" data-type="winner" data-testing-id="tab-gainer">Gainer</button>
            </div>
            <span></span>
            <div class="item tabsDv padHt source-tabs">
              <button class="tab active" data-source="coinmarketcap" data-testing-id="source-coinmarketcap">CoinMarketCap</button>
              <button class="tab" data-source="coingecko" data-testing-id="source-coingecko">CoinGecko</button>
            </div>
            <div class="item">
              <select class="topCoins-select" data-testing-id="top-coins-select">
                <option value="all">All Coins</option>
                <option value="top_100">Top 100</option>
                <option value="top_300">Top 300</option>
                <option value="top_1000">Top 1000</option>
              </select>
            </div>
            <div class="item">
              <select class="period-select" data-testing-id="period-select">
                <option value="1h">1 Hour</option>
                <option value="24h">24 Hours</option>
                <option value="7d">7 Days</option>
                <option value="14d">14 Days</option>
                <option value="30d">30 Days</option>
                <option value="60d">60 Days</option>
                <option value="1y">1 Year</option>
              </select>
            </div>
          </div>
          <div class="chartDv" id="eventsContainer" style="height: 100%; padding: 0; margin: 0;"></div>
        </div>
      </div>
    `;

    // Add styles for tabs
    const style = document.createElement('style');
    style.textContent = ``;
    this.shadowRoot.appendChild(style);
    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    // Add event listeners for tabs
    const typeTabs = this.shadowRoot.querySelectorAll('.type-tabs .tab');
    const sourceTabs = this.shadowRoot.querySelectorAll('.source-tabs .tab');

    typeTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const type = target.dataset.type as 'winner' | 'loser';

        // Update active state
        typeTabs.forEach(t => t.classList.remove('active'));
        target.classList.add('active');

        // Fetch new data with selected type
        this.setupChart(type, undefined);
      });
    });

    sourceTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const source = target.dataset.source as 'coinmarketcap' | 'coingecko';

        // Update active state
        sourceTabs.forEach(t => t.classList.remove('active'));
        target.classList.add('active');

        // Fetch new data with selected source
        this.setupChart(undefined, source);
      });
    });

    // Add event listener for topCoins select
    const topCoinsSelect = this.shadowRoot.querySelector('.topCoins-select') as HTMLSelectElement;
    if (topCoinsSelect) {
      topCoinsSelect.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const topCoins = target.value as 'all' | 'top_100' | 'top_300' | 'top_1000';
        this.setupChart(undefined, undefined, topCoins, undefined);
      });
    }

    // Add event listener for period select
    const periodSelect = this.shadowRoot.querySelector('.period-select') as HTMLSelectElement;
    if (periodSelect) {
      periodSelect.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const period = target.value as '1h' | '24h' | '7d' | '14d' | '30d' | '60d' | '1y';
        this.setupChart(undefined, undefined, undefined, period);
      });
    }

    this.setupChart('loser', 'coinmarketcap', 'all', '1h');  // Default values
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private currentType: 'winner' | 'loser' = 'loser';
  private currentSource: 'coinmarketcap' | 'coingecko' = 'coinmarketcap';
  private currentTopCoins: 'all' | 'top_100' | 'top_300' | 'top_1000' = 'all';
  private currentPeriod: '1h' | '24h' | '7d' | '14d' | '30d' | '60d' | '1y' = '1h';

  private async setupChart(
    type?: 'winner' | 'loser',
    source?: 'coinmarketcap' | 'coingecko',
    topCoins?: 'all' | 'top_100' | 'top_300' | 'top_1000',
    period?: '1h' | '24h' | '7d' | '14d' | '30d' | '60d' | '1y'
  ): Promise<void> {
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    try {
      if (type) this.currentType = type;
      if (source) this.currentSource = source;
      if (topCoins) this.currentTopCoins = topCoins;
      if (period) this.currentPeriod = period;

      const response = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/crypto/crypto_market_winner_loser?limit=10&offset=0&type=${this.currentType}&source=${this.currentSource}&topCoins=${this.currentTopCoins}&period=${this.currentPeriod}`,
        body: { limit: 10, offset: 0, type: this.currentType, source: this.currentSource, topCoins: this.currentTopCoins, period: this.currentPeriod },
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error("Failed to fetch data");
      this.currentData = await response.json();
      if (!this.currentData?.data?.docs || this.currentData.data.docs.length === 0) {
        widgetBox?.removeAttribute('data-testing-id');
      }
      
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
      widgetBox?.removeAttribute('data-testing-id');
    }
  }

  private trendingCoins(): void {
    if (!this.currentData?.data) return;
    let filtered = this.currentData.data;

    this.renderTrendingCoins(filtered);
  }

  private getPrice(item: any): number | null {
    const value = item?.quote?.USD?.price ?? item?.usd;
    return typeof value === 'number' && !isNaN(value) ? value : null;
  }

  private getUsdChange(item: Record<string, unknown>, period: string): number | null {
    // CoinGecko flat fields: usd_1h_change, usd_24h_change, ...
    const geckoValue = item[`usd_${period}_change`];
    if (typeof geckoValue === 'number' && !isNaN(geckoValue)) return geckoValue;

    // CoinMarketCap nested fields: quote.USD.percent_change_1h, ...
    const quote = (item as any)?.quote?.USD;
    const cmcValue = quote?.[`percent_change_${period}`];
    return typeof cmcValue === 'number' && !isNaN(cmcValue) ? cmcValue : null;
  }

  private getVolume24h(item: any): number | null {
    const value = item?.quote?.USD?.volume_24h ?? item?.usd_24h_vol;
    return typeof value === 'number' && !isNaN(value) ? value : null;
  }

  private getRank(item: any): number | string {
    return item?.cmc_rank ?? item?.market_cap_rank ?? '-';
  }

  private renderTrendingCoins(eventsData: any): void {
    const container = this.shadowRoot.querySelector('#eventsContainer');
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (!container) return;

    if (!eventsData?.docs || eventsData.docs.length === 0) {
      widgetBox?.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      return;
    }

    const formatVolume = (num: number): string => {
      if (num >= 1_000_000_000_000) return (num / 1_000_000_000_000).toFixed(2) + 'T';
      if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(2) + 'B';
      if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
      if (num >= 1_000) return (num / 1_000).toFixed(2) + 'K';
      return num.toString();
    };

    const formatPrice = (value: number | null | undefined): string => {
      if (value === null || value === undefined || isNaN(value)) return '-';
      if (value >= 1) {
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      if (value >= 0.01) return `$${value.toFixed(4)}`;
      return `$${value.toFixed(8)}`;
    };

    const formatPercent = (value: number | null | undefined): string => {
      if (value === null || value === undefined || isNaN(value)) return '-';
      const color = value >= 0 ? 'green' : 'red';
      return `<span class="${color}">${value.toFixed(2)}%</span>`;
    };

    const periodLabel = this.currentPeriod.toUpperCase();

    let html = `
    <div class="eventsMain cryptoTopMoversMain customGrid" style="--grid-template-columns: 3fr 1fr 1fr 1fr 1fr;">
      <div class="item header">
        <p>Coin</p>
        <p>Price</p>
        <p>${periodLabel}%</p>
        <p>24H VOL</p>
        <p>Rank</p>
      </div>
  `;

    eventsData.docs.forEach((item: any, idx: number) => {
      const fallbackUrl = `https://cdn-images.traderverse.io/crypto_dummy.svg`;
      const logoUrl = item.image
        || `https://media.traderverse.io/assets/crypto/${(item.symbol || '').toUpperCase().replace('USD', '')}_CRYPTO.svg`;
      const volumeValue = this.getVolume24h(item);
      const price = formatPrice(this.getPrice(item));
      const periodChange = this.getUsdChange(item, this.currentPeriod);
      const volume = volumeValue !== null ? `$${formatVolume(volumeValue)}` : '-';
      const symbol = (item.symbol || '').toUpperCase();

      html += `
      <div class="item body" data-testing-id="top-mover-item-${idx}" data-symbol="${item.symbol}" data-type="${this.currentType}">
        <p>  <img 
            src="${logoUrl}" 
            width="20" 
            height="14" 
            style="vertical-align:middle;" 
            onerror="this.onerror=null;this.src='${fallbackUrl}';"
          />  ${symbol} - ${item.name}</p>
        <p data-testing-id="top-mover-item-${idx}-price">${price}</p>
        <p class="rangeFigure" data-testing-id="top-mover-item-${idx}-change">${formatPercent(periodChange)}</p>
        <p data-testing-id="top-mover-item-${idx}-volume">${volume}</p>
        <p class="stat" data-testing-id="top-mover-item-${idx}-rank">${this.getRank(item)}</p>
      </div>
    `;
    });

    html += `</div>`;
    container.innerHTML = html;
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#eventsContainer') as HTMLElement;
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData.data?.docs)) {
      if (container) container.innerHTML = getNoDataHTML();
      return;
    }
    this.trendingCoins();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.config.symbol = symbol;
    this.setupChart();
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M15.15 20.3C12.3125 20.3 10 17.9875 10 15.15C10 12.3125 12.3125 10 15.15 10C17.9875 10 20.3 12.3125 20.3 15.15C20.3 17.9875 18 20.3 15.15 20.3ZM15.15 11.8625C13.3375 11.8625 11.875 13.3375 11.875 15.1375C11.875 16.9375 13.35 18.4125 15.15 18.4125C16.95 18.4125 18.425 16.9375 18.425 15.1375C18.425 13.3375 16.9625 11.8625 15.15 11.8625Z" fill="currentColor"/> <path d="M15 28.4375C7.5875 28.4375 1.5625 22.4125 1.5625 15C1.5625 7.5875 7.5875 1.5625 15 1.5625C22.4125 1.5625 28.4375 7.5875 28.4375 15C28.4375 22.4125 22.4125 28.4375 15 28.4375ZM15 3.4375C8.625 3.4375 3.4375 8.625 3.4375 15C3.4375 21.375 8.625 26.5625 15 26.5625C21.375 26.5625 26.5625 21.375 26.5625 15C26.5625 8.625 21.375 3.4375 15 3.4375Z" fill="currentColor"/> <path d="M15.0625 22C14.5158 22 14.0625 21.5192 14.0625 20.9394V9.06061C14.0625 8.48081 14.5158 8 15.0625 8C15.6092 8 16.0625 8.48081 16.0625 9.06061V20.9394C16.0625 21.5192 15.6092 22 15.0625 22Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseCryptoTopMovers: typeof TraderverseCryptoTopMovers; } }
if (typeof window !== 'undefined') { window.TraderverseCryptoTopMovers = TraderverseCryptoTopMovers; }